import type { CatHandle } from "./cat";
import type { Cup } from "./types";
import type { SearchMatch } from "./search";
import { attachCupHud } from "./hud-cup";
import { attachSearchHud } from "./hud-search";
import { attachCatDebugHud } from "./hud-cat-debug";

// Root HUD aggregator. Composes three per-panel modules (cup info,
// search, cat debug) and wires the smaller chrome inline (light mode
// toggle, reset button, max-linear-velocity slider). Each sub-module
// owns its DOM IDs and exposes a small API back here.

export type LightMode = "studio" | "flat";

export interface Hud {
  setCount: (n: number) => void;
  showCup: (cup: Cup | null) => void;
  onLightModeChange: (cb: (mode: LightMode) => void) => void;
  /** Notify when the user closes the info panel (× / Esc / Back button). */
  onClose: (cb: () => void) => void;
  /** Show or hide the "Back to shelf" button (visible during focus mode). */
  setBackVisible: (visible: boolean) => void;
  /** Notify when the user clicks the Reset button. Mirrors the R-key
   * shortcut for touch devices (no keyboard). */
  onReset: (cb: () => void) => void;
  /** Show or hide the Reset button (hidden during focus mode — reset is
   * a no-op while a cup is held in focus). */
  setResetVisible: (visible: boolean) => void;
  /** Notify when the debug-stats overlay is toggled on/off. */
  onDebugToggle: (cb: (enabled: boolean) => void) => void;
  /** Hand the cat handle over so the debug panel can render a pose-aware
   * picker — pose tabs at top, only the behaviors valid in the active
   * pose listed below them. The HUD polls `currentPose()` to keep the
   * "active" highlight in sync. */
  setCat: (cat: CatHandle) => void;
  /** Notify on every search input change. Caller computes matches and
   * pushes them back via setSearchResults. */
  onSearchInput: (cb: (query: string) => void) => void;
  /** Notify when the user picks a result (click or Enter). The cup id is
   * the value of the chosen match. */
  onSearchSelect: (cb: (cupId: string) => void) => void;
  /** Render the dropdown contents from the supplied matches. Empty array
   * with a non-empty query renders an "No matches" message; empty query
   * closes the dropdown. */
  setSearchResults: (query: string, matches: SearchMatch[]) => void;
  /** Programmatically clear the search input + dropdown (e.g. after
   * selecting a result so the search UI doesn't linger). */
  clearSearch: () => void;
  /** Notify on every change to the max-linear-velocity slider. The
   * physics layer wires this to its own setMaxLinvel so live tuning
   * takes effect without a reset. */
  onMaxLinvelChange: (cb: (v: number) => void) => void;
  /** Initialize the slider from the physics default so the displayed
   * value matches the live cap on first paint. */
  setMaxLinvel: (v: number) => void;
}

export function createHud(): Hud {
  const cup = attachCupHud();
  const search = attachSearchHud();
  const catDebug = attachCatDebugHud();

  // ── Light mode toggle ───────────────────────────────────────────
  const lightToggle = document.getElementById(
    "light-toggle",
  ) as HTMLButtonElement;
  const lightLabel = document.getElementById(
    "light-toggle-label",
  ) as HTMLElement;
  let lightMode: LightMode = "studio";
  const lightSubscribers: Array<(mode: LightMode) => void> = [];
  lightToggle.addEventListener("click", () => {
    lightMode = lightMode === "studio" ? "flat" : "studio";
    lightToggle.setAttribute("aria-pressed", String(lightMode === "flat"));
    lightLabel.textContent = lightMode === "flat" ? "Flat" : "Studio";
    for (const cb of lightSubscribers) cb(lightMode);
  });

  // ── Reset button ────────────────────────────────────────────────
  const resetButton = document.getElementById(
    "reset-button",
  ) as HTMLButtonElement;
  const resetSubscribers: Array<() => void> = [];
  resetButton.addEventListener("click", () => {
    for (const cb of resetSubscribers) cb();
  });

  // ── Max-linvel slider (physics debug) ───────────────────────────
  const maxVInput = document.getElementById(
    "physics-debug-maxv",
  ) as HTMLInputElement;
  const maxVValue = document.getElementById(
    "physics-debug-maxv-value",
  ) as HTMLElement;
  const maxVSubscribers: Array<(v: number) => void> = [];
  maxVInput.addEventListener("input", () => {
    const v = Number(maxVInput.value);
    maxVValue.textContent = `${v} m/s`;
    for (const cb of maxVSubscribers) cb(v);
  });

  return {
    setCount: cup.setCount,
    showCup: cup.showCup,
    onClose: cup.onClose,
    setBackVisible: cup.setBackVisible,

    onSearchInput: search.onSearchInput,
    onSearchSelect: search.onSearchSelect,
    setSearchResults: search.setSearchResults,
    clearSearch: search.clearSearch,

    onDebugToggle: catDebug.onDebugToggle,
    setCat: catDebug.setCat,

    onLightModeChange: (cb) => lightSubscribers.push(cb),
    onReset: (cb) => resetSubscribers.push(cb),
    setResetVisible: (visible) => {
      resetButton.hidden = !visible;
    },
    onMaxLinvelChange: (cb) => maxVSubscribers.push(cb),
    setMaxLinvel: (v) => {
      maxVInput.value = String(v);
      maxVValue.textContent = `${v} m/s`;
    },
  };
}
